import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';

/**
 * The page's `t`. A key that a locale hasn't translated yet falls back to the
 * English key with its {{placeholders}} filled in: next-translate otherwise
 * returns a missing key raw, placeholders and all. The English file holds
 * only the keys with placeholders.
 */
export function useReceptionistT() {
  const { t, lang } = useLocale('ai-receptionist');
  return {
    lang,
    t: (key: string, query?: Record<string, unknown>) =>
      t(key, query, { default: key }),
  };
}

/** Number and date locale: Arabic copy uses Latin digits, like the rest. */
export function useFormatLocale() {
  const { locale } = useRouter();
  if (!locale) return 'en';
  return locale === 'ar' ? 'ar-u-nu-latn' : locale;
}

export const LANDING_PATH = '/ai-receptionist';
export const SIGNUP_HREF = '/login?preset=bots';
// /docs is the Mintlify site behind a rewrite, not a Next page.
export const PHONE_DOCS_URL = 'https://easy-peasy.ai/docs/agents/phone-calls';
export const TWILIO_PRICING_URL =
  'https://www.twilio.com/en-us/voice/pricing/us';

/**
 * Twilio's US list prices for an AI phone call, read from
 * TWILIO_PRICING_URL on `checkedOn`. ConversationRelay (speech recognition,
 * the voice and barge-in) is billed per minute on top of the inbound
 * minute. Recheck them before changing any copy that quotes them.
 */
export const TWILIO_US = {
  checkedOn: '2026-10-21',
  localNumberPerMonth: 1.15,
  receivePerMinute: 0.0085,
  relayPerMinute: 0.07,
} as const;

export const TWILIO_PER_MINUTE =
  TWILIO_US.receivePerMinute + TWILIO_US.relayPerMinute;

/**
 * Starter, the cheapest plan sold on /chatbots, read from Stripe products in
 * getStaticProps: the monthly price and, when it has one, the yearly price
 * per month. Every paid plan can answer phone calls.
 */
export type PlanPrice = {
  cents: number;
  yearlyCents: number | null;
  currency: string;
} | null;

export function formatMoney(
  amount: number,
  currency: string,
  locale: string,
  digits?: number,
) {
  const fraction = digits ?? (Number.isInteger(amount) ? 0 : 2);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
  }).format(amount);
}

/** Starter's monthly price, formatted, or null when it couldn't be read. */
export function usePlanPrice(price: PlanPrice) {
  const locale = useFormatLocale();
  return price ? formatMoney(price.cents / 100, price.currency, locale) : null;
}

/** Starter's price per month when billed yearly, formatted. */
export function useYearlyPlanPrice(price: PlanPrice) {
  const locale = useFormatLocale();
  return price?.yearlyCents
    ? formatMoney(price.yearlyCents / 100, price.currency, locale)
    : null;
}

/**
 * The demo business in the page's mock-ups: a fictional plumbing company,
 * in its own colour (deliberately not ours). Names and numbers are never
 * translated; phone numbers stay in the 555-0100 to 555-0199 range, which
 * is reserved for fiction and can't be dialled.
 */
export const BUSINESS = 'Larkfield Plumbing';
export const BUSINESS_INITIAL = 'L';
export const BUSINESS_NUMBER = '+1 (512) 555-0199';
export const CALLER_NUMBER = '+1 (512) 555-0147';
export const CALLER_NAME = 'Dana Ruiz';
/**
 * Secondary text. The kit's `tone.quiet` falls just under 4.5:1 on this
 * page's tinted panels and dark cards; this one passes AA on both.
 */
export const QUIET = 'text-[#66667e] dark:text-[#8e8eaa]';

export const BRAND_BG = 'bg-[#11795f]';
export const BRAND_SOFT =
  'bg-[#11795f]/10 text-[#0c5c48] dark:bg-[#11795f]/25 dark:text-[#8fe3c6]';

type GlyphProps = { className?: string };

export function PhoneGlyph({ className }: GlyphProps) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      aria-hidden='true'
      className={className}
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z'
      />
    </svg>
  );
}

export function CalendarGlyph({ className }: GlyphProps) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      aria-hidden='true'
      className={className}
    >
      <rect x='3.25' y='4.75' width='17.5' height='16' rx='3' />
      <path strokeLinecap='round' d='M3.25 9.75h17.5M8 2.75v4M16 2.75v4' />
    </svg>
  );
}

export function ExternalGlyph({ className }: GlyphProps) {
  return (
    <svg
      viewBox='0 0 20 20'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      aria-hidden='true'
      className={className}
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M8 4H5.5A1.5 1.5 0 004 5.5v9A1.5 1.5 0 005.5 16h9a1.5 1.5 0 001.5-1.5V12M11 4h5v5M16 4l-7 7'
      />
    </svg>
  );
}

/** A fixed, hand-shaped waveform: loud in the middle of a phrase. */
export const WAVE = [
  5, 9, 14, 10, 18, 24, 15, 28, 34, 22, 38, 30, 42, 33, 26, 36, 28, 20, 30, 22,
  16, 24, 14, 10, 16, 8, 6,
];
